import { useFormik } from 'formik'
import { FC } from 'react'
import {
  Button,
  FormInput,
  FormSelect,
  InfoAlert,
  IOption,
  useModal,
} from '../../../../../../components'
import {
  ButtonType,
  OptionsHelper,
  Receituario,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import { termoUsoDoReceituario } from '../../core/receituario'
import { medicamentoSchema } from '../../validation/medicamentoSchema'
import { updateMedicamento } from '../../core/_requests'
import { CustomOption, MedicamentoDtoCadastro } from '../../core/_model'
import { useT } from '_metronic/i18n/Metronici18n'

type IProps = {
  onConfirm: (item: CustomOption) => void
  item?: CustomOption
}

export const MedicamentoModal: FC<IProps> = ({ onConfirm, item }) => {
  const { closeModal } = useModal()
  const { t } = useT()
  const id = item?.value ? Number(item.value) : undefined
  const isPersisted = !!id

  const initialValues = {
    medicamento: item?.label || '',
    concentracao: item?.concentracao || '',
    apresentacao: item?.apresentacao || '',
    receituario: OptionsHelper.receituarios.find((o) => o.value === item?.receituario) as
      | IOption<Receituario>
      | undefined,
  }

  const formik = useFormik({
    initialValues,
    validationSchema: medicamentoSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        let result: CustomOption

        if (isPersisted && id) {
          const data: MedicamentoDtoCadastro = {
            nome: values.medicamento,
            codigo: item?.codigo,
            concentracao: values.concentracao || undefined,
            apresentacao: values.apresentacao || undefined,
            receituario: values.receituario?.value,
            ativo: item?.ativo ?? true,
          }

          const updated = await updateMedicamento(id, data)

          result = {
            value: updated.id,
            label: updated.nome,
            codigo: updated.codigo,
            concentracao: updated.concentracao,
            apresentacao: updated.apresentacao,
            classe: updated.classe,
            tarja: updated.tarja,
            receituario: updated.receituario,
          }

          await showAlert({
            title: t('common.success'),
            text: t('alert.atualizar', {
              gender: t('medicamento.gender'),
              name: t('medicamento.label'),
            }),
            icon: 'success',
          })
        } else {
          result = {
            label: values.medicamento,
            concentracao: values.concentracao || undefined,
            apresentacao: values.apresentacao || undefined,
            receituario: values.receituario?.value,
            value: null,
          }
        }

        onConfirm(result)
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  // Informativo: o termo é da lista (retinoide, anorexígeno), o médico não escolhe.
  const termoUso = termoUsoDoReceituario(formik.values.receituario?.value)

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <div className='row'>
        <div className='col-md-12 mb-3'>
          <FormInput formik={formik} name='medicamento' entity='medicamento' isRequired />
        </div>
        <div className='col-md-6 mb-3'>
          <FormInput formik={formik} name='concentracao' entity='concentracao' />
        </div>
        <div className='col-md-6 mb-3'>
          <FormInput formik={formik} name='apresentacao' entity='apresentacao' />
        </div>
        <FormSelect
          formik={formik}
          name='receituario'
          entity='receituario'
          inputId='medicamento-prescricao-receituario'
          options={OptionsHelper.receituarios}
          className='mb-3'
          isDisabled={formik.isSubmitting}
          isClearable={false}
          isRequired
        />
        {termoUso && (
          <InfoAlert variant='info' role='note' className='mb-3'>
            {t('medicamento.exige_termo', { termo: termoUso })}
          </InfoAlert>
        )}
      </div>

      <div className='d-flex justify-content-end mt-5'>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {isPersisted ? t('common.save') : t('common.create')}
        </Button>
      </div>
    </form>
  )
}
